import httpService from "./httpService";
import type { User } from "../contexts/AuthContext";
import { getOAuthResponseUrl } from "../utils/oauthContinuation";

interface OAuthRedirectResponse {
  redirect: boolean;
  url: string;
}

interface OAuthWebsiteLoginResponse extends OAuthRedirectResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface OAuthAccount {
  id: string;
  email: string;
}

export interface OAuthClientMetadata {
  client_id: string;
  client_name?: string;
}

const providerRequestConfig = { requiresAuth: false, credentials: "include" } as const;

export async function completeOAuthLogin(
  loginPath: "/verify-otp" | "/sign-in/social",
  loginCredentials: ({ email: string; otp: string } | { provider: "google"; idToken: { token: string } }) & { anonymousId?: string },
  oauthQuery: string
): Promise<OAuthWebsiteLoginResponse> {
  if (!oauthQuery) throw new Error("Invalid authorization request. Restart the connection from your app.");
  const loginResponse = await httpService.postAuth<OAuthWebsiteLoginResponse>(
    loginPath,
    { ...loginCredentials, oauth_query: oauthQuery },
    providerRequestConfig
  );
  const websiteLogin = loginResponse.data;
  if (websiteLogin?.redirect !== true
    || typeof websiteLogin.accessToken !== "string" || !websiteLogin.accessToken.trim()
    || typeof websiteLogin.refreshToken !== "string" || !websiteLogin.refreshToken.trim()
    || typeof websiteLogin.user?.id !== "string" || !websiteLogin.user.id
    || typeof websiteLogin.user?.email !== "string" || !websiteLogin.user.email) {
    throw new Error("The authorization server did not complete website sign-in. Restart the connection from your app.");
  }
  return {
    redirect: true,
    url: getOAuthResponseUrl(websiteLogin.url),
    user: websiteLogin.user,
    accessToken: websiteLogin.accessToken,
    refreshToken: websiteLogin.refreshToken,
  };
}

export async function getOAuthClientMetadata(clientId: string, signal: AbortSignal): Promise<OAuthClientMetadata> {
  const clientResponse = await httpService.get<OAuthClientMetadata>(
    `/api/auth-internal/oauth2/public-client?${new URLSearchParams({ client_id: clientId })}`,
    { ...providerRequestConfig, signal }
  );
  if (clientResponse.data?.client_id !== clientId
    || (clientResponse.data.client_name !== undefined && typeof clientResponse.data.client_name !== "string")) {
    throw new Error("Invalid application details");
  }
  return clientResponse.data;
}

export async function getOAuthAccount(signal: AbortSignal): Promise<OAuthAccount> {
  const sessionResponse = await httpService.get<{ user?: OAuthAccount } | null>(
    "/api/auth-internal/get-session?disableCookieCache=true",
    { ...providerRequestConfig, signal }
  );
  const providerAccount = sessionResponse.data?.user;
  if (!providerAccount) {
    throw Object.assign(new Error("Sign in again to connect your account."), { status: 401 });
  }
  if (typeof providerAccount.id !== "string" || !providerAccount.id
    || typeof providerAccount.email !== "string" || !providerAccount.email) {
    throw new Error("The authorization server returned an invalid account.");
  }
  return { id: providerAccount.id, email: providerAccount.email };
}

export async function selectOAuthAccount(oauthQuery: string, accountId: string): Promise<string> {
  const selectionResponse = await httpService.post<OAuthRedirectResponse>(
    "/api/auth-internal/oauth2/continue",
    { selected: true, oauth_query: oauthQuery },
    { ...providerRequestConfig, headers: { "X-Mcp-Account-Id": accountId } }
  );
  if (selectionResponse.data?.redirect !== true) {
    throw new Error("The authorization server did not complete account selection. Restart the connection from your app.");
  }
  return getOAuthResponseUrl(selectionResponse.data.url);
}

export async function submitOAuthConsent(accept: boolean, oauthQuery: string, accountId: string): Promise<string> {
  const consentResponse = await httpService.post<OAuthRedirectResponse>(
    "/api/auth-internal/oauth2/consent",
    { accept, oauth_query: oauthQuery },
    { ...providerRequestConfig, headers: { "X-Mcp-Account-Id": accountId } }
  );
  if (consentResponse.data?.redirect !== true) {
    throw new Error("The authorization server did not complete consent. Restart the connection from your app.");
  }
  return getOAuthResponseUrl(consentResponse.data.url);
}
